import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { lumi } from '@/lib/lumi'
import { gerarCertificadoPdfBase64 } from '@/utils/certificadoPdf'
import PhoneInput, { type PaisOpcao, PAISES, validarNumero } from '@/components/PhoneInput'
import {CheckCircle2, Lock, PlayCircle, Award, ArrowRight, Loader2, AlertCircle, RefreshCw, Leaf} from 'lucide-react'

// ─── Dados das 7 aulas ────────────────────────────────────────────────────────

interface Pergunta {
  enunciado: string
  opcoes: string[]
  correta: number // índice da opção correta
}

interface Aula {
  id: string
  numero: number
  titulo: string
  descricao: string
  videoUrl: string
  quiz: Pergunta[]
}

const AULAS: Aula[] = [
  {
    id: 'aula1',
    numero: 1,
    titulo: 'Será que realmente funciona um óleo essencial?',
    descricao:
      'Nesta aula você vai descobrir o que é um óleo essencial, como utilizar com segurança e ainda vai conhecer a história de Mônica Maria, onde um erro médico, uma dor profunda e os óleos essenciais mudaram tudo.',
    videoUrl: 'https://www.youtube.com/embed/ZK2jIJ1gum0',
    quiz: [
      {
        enunciado: 'Sobre os Óleos Essenciais, qual alternativa é INCORRETA?',
        opcoes: [
          'Os óleos essenciais são extratos puros das plantas',
          'São altamente concentrados sendo de 50 a 70 vezes mais potentes que ervas',
          '1 gota do óleo essencial de hortelã pimenta equivale a 28 xícaras de chá de hortelã',
          'Os óleos essenciais são menos poderosos que os remédios',
        ],
        correta: 3,
      },
      {
        enunciado: 'Quais são as formas de uso dos óleos essenciais?',
        opcoes: ['Aromático', 'Tópico', 'Interno', 'Todas as alternativas'],
        correta: 3,
      },
      {
        enunciado: 'Sobre os óleos essenciais, o que é INCORRETO afirmar?',
        opcoes: [
          'Os óleos essenciais podem ser usados por todas as pessoas',
          'Os óleos essenciais não podem ser usados em crianças e animais',
          'Os óleos essenciais são encontrados em sementes, flores e folhas',
          'Os óleos essenciais são compostos que protegem as plantas contra ameaças ambientais',
        ],
        correta: 1,
      },
    ],
  },
  {
    id: 'aula2',
    numero: 2,
    titulo: 'O óleo essencial não é apenas um cheirinho',
    descricao:
      'Você vai aprender sobre o uso tópico, a importância da diluição com óleo vegetal e o poder das combinações de óleos essenciais.',
    videoUrl: 'https://www.youtube.com/embed/0LKpE_pWicY',
    quiz: [
      {
        enunciado: 'O que é INCORRETO afirmar sobre os óleos essenciais?',
        opcoes: [
          'O óleo essencial é indicado para usar de forma paliativa',
          'O óleo essencial é capaz de mudar rapidamente seu estado interno',
          'Um óleo essencial pode ser misturado com outro potencializando seu efeito',
          'No uso direto na pele a regra geral é diluir com um óleo vegetal',
        ],
        correta: 0,
      },
      {
        enunciado: 'Sobre o uso dos óleos essenciais é INCORRETO afirmar:',
        opcoes: [
          'Os óleos essenciais quando diluídos com óleo vegetal não perdem a potência',
          'Os óleos essenciais nunca devem ser misturados com outros óleos essenciais',
          'Os óleos essenciais quando diluídos são absorvidos na pele por completo',
          'No uso tópico o óleo é absorvido em média em 20 minutos',
        ],
        correta: 1,
      },
      {
        enunciado: 'Sobre os óleos essenciais qual a alternativa CORRETA?',
        opcoes: [
          'Os óleos essenciais dão suporte em desconfortos emocionais',
          'Os óleos essenciais agem rapidamente na via aromática',
          'Vários óleos essenciais podem ser ingeridos',
          'Todas as alternativas são corretas',
        ],
        correta: 3,
      },
    ],
  },
  {
    id: 'aula3',
    numero: 3,
    titulo: 'Óleo Essencial de Lavanda e Tangerina',
    descricao:
      'Nesta aula você vai descobrir as principais aplicações físicas e emocionais dos óleos essenciais de Lavanda e Tangerina.',
    videoUrl: 'https://www.youtube.com/embed/7EEC6aPQ8q8',
    quiz: [
      {
        enunciado: 'Quais são os principais usos do óleo essencial de Lavanda?',
        opcoes: [
          'Insônia e problemas de sono',
          'Estresse, ansiedade e arritmia',
          'Dor, inflamação e pressão alta',
          'Todas as alternativas',
        ],
        correta: 3,
      },
      {
        enunciado: 'O óleo essencial de Tangerina é conhecido como:',
        opcoes: [
          'Óleo essencial da restauração e fé',
          'Óleo essencial da espontaneidade, criatividade e diversão',
          'Óleo essencial da segurança energética',
          'Óleo essencial da fala confiante',
        ],
        correta: 1,
      },
      {
        enunciado: 'Qual é o melhor tipo de difusor?',
        opcoes: [
          'Qualquer um é indicado',
          'Sem nenhum tipo de certificação de segurança',
          'Ultrassônico e que não aqueça',
          'Elétrico de plástico (tomada)',
        ],
        correta: 2,
      },
    ],
  },
  {
    id: 'aula4',
    numero: 4,
    titulo: 'Óleo Essencial de Hortelã-Pimenta e Limão Siciliano',
    descricao:
      'Você vai conhecer dois óleos essenciais incríveis para elevar sua energia e alegria.',
    videoUrl: 'https://www.youtube.com/embed/1GsLSpMmuxw',
    quiz: [
      {
        enunciado:
          'Quais são os principais usos do óleo essencial de Hortelã-pimenta?',
        opcoes: [
          'Pressão alta e pele seca',
          'Problemas de sono e irritação',
          'Dores de cabeça, pressão baixa, desconfortos respiratórios e digestivos',
          'Queimadura e hiperatividade',
        ],
        correta: 2,
      },
      {
        enunciado: 'O óleo essencial de Hortelã-pimenta é:',
        opcoes: [
          'Aterrador',
          'Relaxante',
          'Energizante e relaxante',
          'Energizante',
        ],
        correta: 3,
      },
      {
        enunciado: 'O óleo essencial de Limão Siciliano é indicado para:',
        opcoes: [
          'Antiviral',
          'Limpeza linfática',
          'Melhorar a digestão',
          'Todas as alternativas',
        ],
        correta: 3,
      },
    ],
  },
  {
    id: 'aula5',
    numero: 5,
    titulo: 'Óleo Essencial de Melaleuca e On Guard',
    descricao:
      'Conheça dois óleos essenciais poderosos para fortalecer sua saúde com ação antiviral, anti-inflamatória e imunoprotetora.',
    videoUrl: 'https://www.youtube.com/embed/b24hGZ4Zu-c',
    quiz: [
      {
        enunciado: 'O óleo essencial de Melaleuca é indicado para:',
        opcoes: [
          'Cortes e ferimentos',
          'Bactérias e viroses',
          'Caspa e acne',
          'Todas as alternativas',
        ],
        correta: 3,
      },
      {
        enunciado: 'O mix de óleos essenciais On Guard é indicado para:',
        opcoes: [
          'Insônia e relaxar',
          'Aumentar a imunidade',
          'Cortes e queimaduras',
          'Hiperatividade',
        ],
        correta: 1,
      },
      {
        enunciado: 'Sobre o Mix Protetor On Guard o que é CORRETO afirmar?',
        opcoes: [
          'É uma mistura energizante',
          'É importante diluir antes do uso tópico pois há canela em sua composição',
          'É indicado para sistema imunológico e respiratório',
          'Todas as alternativas são corretas',
        ],
        correta: 3,
      },
    ],
  },
  {
    id: 'aula6',
    numero: 6,
    titulo: 'Óleo Essencial de Copaíba, Olíbano e Balance',
    descricao:
      'Conheça o rei e a rainha dos óleos essenciais bem como uma mistura estabilizadora que promove paz e equilíbrio emocional.',
    videoUrl: 'https://www.youtube.com/embed/EB0plU1EXL0',
    quiz: [
      {
        enunciado: 'Sobre o óleo essencial de Copaíba marque a INCORRETA:',
        opcoes: [
          'É indicado para sistema respiratório, cardiovascular e equilíbrio emocional',
          'É indicado para dores no corpo',
          'É indicado para energizar e alegrar',
          'É indicado para estresse e ansiedade',
        ],
        correta: 2,
      },
      {
        enunciado: 'O óleo essencial de Olíbano é indicado para:',
        opcoes: [
          'Relaxar e equilíbrio emocional',
          'Manchas e problemas na pele',
          'Ansiedade e depressão',
          'Todas as alternativas',
        ],
        correta: 3,
      },
      {
        enunciado: 'Sobre o mix Balance NÃO é indicado para:',
        opcoes: [
          'Dores musculares, tensão e estresse',
          'Alegrar, revigorar e energizar',
          'Medo, ansiedade e depressão',
          'Hiperatividade e autismo',
        ],
        correta: 1,
      },
    ],
  },
  {
    id: 'aula7',
    numero: 7,
    titulo: 'Mix de Óleos Essenciais: Breathe, Zengest e Deep Blue',
    descricao:
      'Conheça 3 misturas poderosas: Breathe para apoio respiratório, Zengest para equilíbrio digestivo e Deep Blue para alívio de dores.',
    videoUrl: 'https://www.youtube.com/embed/pmssweXXjV0',
    quiz: [
      {
        enunciado: 'Sobre a Mistura Breathe marque a alternativa CORRETA:',
        opcoes: [
          'É indicado para gripe, tosse e congestão',
          'É relaxante',
          'É indicado para melhorar o sono',
          'Todas as alternativas',
        ],
        correta: 3,
      },
      {
        enunciado: 'Sobre o Mix Zengest é INCORRETO afirmar:',
        opcoes: [
          'É indicado para refluxo, gases e indigestão',
          'Pode ser diluída com óleo vegetal e aplicar no estômago',
          'É relaxante',
          'É energizante',
        ],
        correta: 2,
      },
      {
        enunciado: 'A Mistura Deep Blue é indicada para:',
        opcoes: [
          'Para sair do pânico e aflição',
          'Dores de cabeça e enxaqueca',
          'Dores, tensão e inflamações no corpo',
          'Todas as alternativas',
        ],
        correta: 3,
      },
    ],
  },
]

// ─── URL da Deno Function ─────────────────────────────────────────────────────
const EMAIL_FN_URL =
  'https://api.lumi.new/v1/functions/p427216140118241280/send-welcome-email'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function labelEtapa(etapa: string): string {
  const map: Record<string, string> = {
    selecao_concluida: 'Inscrição concluída',
    diagnostico_concluido: 'Diagnóstico preenchido',
    aula1: 'Em andamento (aula 1 de 7)',
    aula2: 'Em andamento (aula 2 de 7)',
    aula3: 'Em andamento (aula 3 de 7)',
    aula4: 'Em andamento (aula 4 de 7)',
    aula5: 'Em andamento (aula 5 de 7)',
    aula6: 'Em andamento (aula 6 de 7)',
    aula7: 'Em andamento (aula 7 de 7)',
    certificado_emitido: 'Curso concluído',
    acesso_criado: 'Acesso criado',
  }
  return map[etapa] ?? etapa
}

// ─── Componente principal ─────────────────────────────────────────────────────

function Curso() {
  const navigate = useNavigate()

  // Registro da participante na coleção capacitacao
  const [registro, setRegistro] = useState<any>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState('')

  // Aula aberta no modal
  const [aulaAtiva, setAulaAtiva] = useState<Aula | null>(null)

  // Controle por aula: assistiu o vídeo?
  const [assistiu, setAssistiu] = useState(false)

  // Respostas do quiz (índice da opção selecionada por pergunta)
  const [respostas, setRespostas] = useState<(number | null)[]>([null, null, null])

  // Feedback após confirmar quiz
  type FeedbackQuiz = null | { acertos: number; detalhes: boolean[] }
  const [feedbackQuiz, setFeedbackQuiz] = useState<FeedbackQuiz>(null)

  const [salvando, setSalvando] = useState(false)

  // Identificação de emergência: solicitada apenas quando a última aula é
  // concluída sem nenhum registro vinculado em `capacitacao` (acesso direto
  // a /curso-aulas, fora dos fluxos de Seleção, Curso ou Capacitação Equipe).
  const [precisaIdentificar, setPrecisaIdentificar] = useState(false)
  const [identNome, setIdentNome] = useState('')
  const [identEmail, setIdentEmail] = useState('')
  const [identErro, setIdentErro] = useState('')
  const [criandoIdentidade, setCriandoIdentidade] = useState(false)
  const [conclusaoPendente, setConclusaoPendente] = useState<{ novasCompletas: string[] } | null>(null)
  const [identWhatsapp, setIdentWhatsapp] = useState('')
  const [identPais, setIdentPais] = useState<PaisOpcao>(PAISES[0])

  // ── Carregar registro ────────────────────────────────────────────────────────

  useEffect(() => {
    void carregarRegistro()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function carregarRegistro() {
    setCarregando(true)
    setErro('')
    try {
      // ── FORMA 1: via parâmetros ?email= e ?token= na URL (link do e-mail) ──
      const params = new URLSearchParams(window.location.search)
      const emailParam = params.get('email')?.trim() ?? ''
      const tokenParam = params.get('token')?.trim() ?? ''

      if (emailParam && tokenParam) {
        const resp = await lumi.entities.capacitacao.list({
          filter: { email: emailParam },
          limit: 1,
        })
        const item = resp?.list?.[0]
        if (!item) {
          setErro('Link inválido ou inscrição não encontrada. Verifique seu e-mail ou refaça o cadastro na seleção.')
          setCarregando(false)
          return
        }
        // Valida se o token bate com o registrado
        const tokenSalvo = item.tokenAcesso ?? ''
        if (tokenSalvo !== tokenParam) {
          setErro('Link inválido ou expirado. Acesse o último e-mail recebido ou refaça o cadastro na seleção.')
          setCarregando(false)
          return
        }
        // Token válido — salvar sessão para manter identificação durante a visita
        try {
          sessionStorage.setItem('capacitacao_whatsapp', item.whatsapp ?? '')
          sessionStorage.setItem('capacitacao_nome', item.nome ?? '')
          sessionStorage.setItem('capacitacao_email', item.email ?? '')
          sessionStorage.setItem('capacitacao_token', tokenParam)
          const id = (item as any)?._id ?? ''
          if (id) sessionStorage.setItem('capacitacao_id', id)
        } catch {
          // ignore
        }
        // Limpar params da URL sem recarregar a página (UX mais limpa)
        try {
          const url = new URL(window.location.href)
          url.searchParams.delete('email')
          url.searchParams.delete('token')
          window.history.replaceState({}, '', url.toString())
        } catch {
          // ignore
        }
        setRegistro(item)
        setCarregando(false)
        return
      }

      // ── FORMA 2: via identificadores salvos na sessão ──
      // Os fluxos de Seleção, Curso (/curso/inscricao) e Capacitação Equipe
      // gravam capacitacao_whatsapp/capacitacao_email tanto em sessionStorage
      // quanto em localStorage logo após criar o registro em `capacitacao`,
      // garantindo que este player encontre o registro correto como fonte
      // única de verdade, mesmo se a sessão do navegador for reaberta.
      const lerIdentificador = (chave: string): string => {
        try {
          return sessionStorage.getItem(chave) || localStorage.getItem(chave) || ''
        } catch {
          return ''
        }
      }

      const whatsapp = lerIdentificador('capacitacao_whatsapp')
      const emailSessao = lerIdentificador('capacitacao_email')

      let item: any = null
      if (whatsapp) {
        const resp = await lumi.entities.capacitacao.list({
          filter: { whatsapp },
          limit: 1,
        })
        item = resp?.list?.[0] ?? null
      }
      if (!item && emailSessao) {
        const resp = await lumi.entities.capacitacao.list({
          filter: { email: emailSessao },
          limit: 1,
        })
        item = resp?.list?.[0] ?? null
      }

      if (!item) {
        // Sem nenhuma identificação encontrada: acesso direto/anônimo a
        // /curso-aulas. O player abre normalmente; a identificação (nome +
        // e-mail) será solicitada apenas ao concluir a última aula, para
        // garantir persistência em `capacitacao`, certificado e e-mails
        // também nesse cenário.
        setRegistro(null)
        setCarregando(false)
        return
      }

      // Reforça os identificadores em ambos os storages para as próximas visitas
      try {
        const id = (item as any)?._id ?? ''
        if (id) {
          sessionStorage.setItem('capacitacao_id', id)
          localStorage.setItem('capacitacao_id', id)
        }
        sessionStorage.setItem('capacitacao_whatsapp', item.whatsapp ?? whatsapp)
        sessionStorage.setItem('capacitacao_nome', item.nome ?? '')
        sessionStorage.setItem('capacitacao_email', item.email ?? emailSessao)
        localStorage.setItem('capacitacao_whatsapp', item.whatsapp ?? whatsapp)
        localStorage.setItem('capacitacao_nome', item.nome ?? '')
        localStorage.setItem('capacitacao_email', item.email ?? emailSessao)
      } catch {
        // ignore
      }

      setRegistro(item)
    } catch (e) {
      console.error('[curso] erro ao carregar registro', e)
      setErro('Não foi possível carregar seu progresso. Tente novamente.')
    } finally {
      setCarregando(false)
    }
  }

  // ── Helpers de progresso ──────────────────────────────────────────────────────

  function aulasCompletas(): string[] {
    return Array.isArray(registro?.aulasCompletas) ? registro.aulasCompletas : []
  }

  function aulaLiberada(numero: number): boolean {
    if (numero === 1) return true
    return aulasCompletas().includes(`aula${numero - 1}`)
  }

  function aulaConcluida(id: string): boolean {
    return aulasCompletas().includes(id)
  }

  // ── Abrir/fechar modal ────────────────────────────────────────────────────────

  function abrirAula(aula: Aula) {
    if (!aulaLiberada(aula.numero)) return
    setAulaAtiva(aula)
    setAssistiu(false)
    setRespostas([null, null, null])
    setFeedbackQuiz(null)
  }

  function fecharModal() {
    setAulaAtiva(null)
    setAssistiu(false)
    setRespostas([null, null, null])
    setFeedbackQuiz(null)
  }

  // ── Certificado em PDF + disparo de e-mails 4/8/9 (compartilhado) ──────────────
  // Usado tanto pelo fluxo normal (registro já vinculado em `capacitacao`) quanto
  // pelo fluxo de identificação de emergência (registro criado no momento da
  // conclusão), garantindo que ambos os caminhos gerem o PDF e disparem os
  // mesmos e-mails ao concluir o curso.
  async function enviarEmailsConclusao(
    registroFinal: any,
    novoToken: string,
    dataFimAcessoFinal: string,
  ) {
    const agora = new Date().toISOString()
    const nome = registroFinal?.nome ?? ''
    const email = registroFinal?.email ?? ''
    const whatsapp = registroFinal?.whatsapp ?? ''
    const statusTrabalho = registroFinal?.status ?? ''
    const relacaoDoterra = registroFinal?.relacaoDoterra ?? ''
    const indicadoPor = registroFinal?.indicadoPor ?? ''
    const dataExpiracao = dataFimAcessoFinal
    const linkCriarSenha = `https://www.descubraseusoleos.com.br/criar-senha?token=${encodeURIComponent(novoToken)}&email=${encodeURIComponent(email)}`
    const linkPersonalizado = `https://www.descubraseusoleos.com.br?c=${(nome || '').toLowerCase().replace(/\s+/g, '')}`

    // Slug do nome para o nome do arquivo PDF
    const slugNome = (nome || 'aluno')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
    const pdfFilename = `certificado-${slugNome}-uma-gota-de-equilibrio.pdf`

    // PASSO 1 — Gerar PDF do certificado ANTES do disparo dos e-mails
    // (gerador vetorial via jsPDF — síncrono, leve e fiel ao layout aprovado)
    let pdfBase64 = ''
    try {
      pdfBase64 = await gerarCertificadoPdfBase64(nome, agora)
      console.log('[curso] PDF gerado com sucesso, tamanho base64:', pdfBase64.length)
    } catch (err) {
      console.error('[curso] erro ao gerar PDF do certificado', err)
    }

    const attachments = pdfBase64
      ? [{ filename: pdfFilename, content: pdfBase64 }]
      : undefined

    // PASSO 2 — Buscar consultora indicadora (se houver) ANTES dos disparos
    let consultoraIndicou: any = null
    let indicadoPorNome = ''
    let indicadoPorWhatsapp = ''
    if (indicadoPor && indicadoPor.toLowerCase() !== 'monica') {
      try {
        const respC = await lumi.entities.consultoras.list({
          filter: { usuario: indicadoPor },
          limit: 1,
        })
        consultoraIndicou = respC?.list?.[0]
        if (consultoraIndicou) {
          indicadoPorNome = consultoraIndicou.nome ?? ''
          indicadoPorWhatsapp = consultoraIndicou.whatsapp ?? ''
        }
      } catch (err) {
        console.error('[curso] erro ao buscar consultora indicadora', err)
      }
    }

    // PASSO 3 — Disparar E-mail 4 com PDF anexado (com AWAIT, sem keepalive)
    // keepalive tem limite de 64KB e descarta payloads maiores como anexos PDF
    try {
      const resp4 = await fetch(EMAIL_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo: 'curso_aluno',
          nome,
          email,
          // Link público de download direto (sem login) — verifica email+token
          linkCertificado: `https://www.descubraseusoleos.com.br/certificado/download?email=${encodeURIComponent(email)}&token=${encodeURIComponent(novoToken)}`,
          linkCriarSenha,
          linkPersonalizado,
          loginEmail: email,
          dataExpiracao,
          indicadoPorNome,
          indicadoPorWhatsapp,
          attachments,
        }),
      })
      const data4 = await resp4.json().catch(() => ({}))
      console.log('[curso] e-mail 4 enviado:', resp4.status, data4)
    } catch (err) {
      console.error('[curso] erro e-mail 4', err)
    }

    // PASSO 4 — Disparar E-mail 8 (Mônica) — sem anexo, pode usar fire-and-forget
    void fetch(EMAIL_FN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tipo: 'curso_admin',
        nome,
        nomeAluno: nome,
        email: 'contato@descubraseusoleos.com.br',
        emailAluno: email,
        whatsapp,
        whatsappAluno: whatsapp,
        statusTrabalho,
        relacaoDoterra,
        indicadoPor: indicadoPor || 'acesso direto',
        dataConclusao: agora,
        dataExpiracao,
      }),
    }).catch(err => console.error('[curso] erro e-mail 8', err))

    // PASSO 5 — Disparar E-mail 9 (consultora que indicou) — se houver
    if (consultoraIndicou?.email) {
      void fetch(EMAIL_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo: 'curso_indicacao',
          nome: consultoraIndicou.nome,
          email: consultoraIndicou.email,
          nomeAluno: nome,
          whatsappAluno: whatsapp,
        }),
      }).catch(err => console.error('[curso] erro e-mail 9', err))
    }
  }

  // ── Confirmar quiz ────────────────────────────────────────────────────────────

  async function confirmarQuiz() {
    if (!aulaAtiva) return
    if (respostas.some(r => r === null)) return

    const detalhes = aulaAtiva.quiz.map((p, i) => respostas[i] === p.correta)
    const acertos = detalhes.filter(Boolean).length

    setFeedbackQuiz({ acertos, detalhes })

    // Mínimo de 2/3 para avançar
    if (acertos < 2) return

    // Se já estava concluída não grava de novo
    if (aulaConcluida(aulaAtiva.id)) {
      setTimeout(() => fecharModal(), 900)
      return
    }

    setSalvando(true)
    try {
      const novasCompletas = [...aulasCompletas(), aulaAtiva.id]
      const todasConcluidas = AULAS.every(a => novasCompletas.includes(a.id))
      const agora = new Date().toISOString()

      const proximaEtapa = todasConcluidas
        ? 'certificado_emitido'
        : `aula${aulaAtiva.numero + 1}`

      // ── Sem registro vinculado (acesso direto/anônimo) e curso concluído agora:
      // solicitar identificação (nome, e-mail, WhatsApp) antes de persistir,
      // gerar o PDF do certificado e disparar os e-mails, garantindo que este
      // cenário também finalize corretamente na coleção `capacitacao`.
      if ((!registro || !registro._id) && todasConcluidas) {
        setConclusaoPendente({ novasCompletas })
        setPrecisaIdentificar(true)
        setSalvando(false)
        fecharModal()
        return
      }

      // ── Sem registro vinculado, aula intermediária: progresso apenas local ──
      // Nesse caso ainda não há documento em `capacitacao` vinculado à sessão;
      // o progresso segue local até a última aula, quando a identificação acima
      // é solicitada para persistir tudo de uma vez.
      if (!registro || !registro._id) {
        const novoRegistroLocal = {
          aulasCompletas: novasCompletas,
          etapaAtual: proximaEtapa,
          certificadoEmitido: false,
        }
        setRegistro(novoRegistroLocal)
        setTimeout(() => fecharModal(), 900)
        return
      }

      // Gerar token de acesso (168h = 7 dias) ao concluir o curso — formato base64(JSON{email, exp})
      // Campo separado tokenCriarSenha — não sobrescreve o tokenAcesso do curso
      const exp7d = Date.now() + 1000 * 60 * 60 * 24 * 7
      const novoToken = todasConcluidas
        ? btoa(JSON.stringify({ email: registro?.email ?? '', exp: exp7d }))
        : registro?.tokenAcesso ?? ''
      const tokenExpira = todasConcluidas
        ? new Date(exp7d).toISOString()
        : registro?.tokenAcessoExpiracao ?? ''

      // Garantir janela de acesso de 21 dias (caso a inscrição antiga não tenha)
      const dataInicioAcesso = registro?.dataInicioAcesso || agora
      const dataFimAcesso = registro?.dataFimAcesso
        || new Date(Date.now() + 1000 * 60 * 60 * 24 * 21).toISOString()

      await lumi.entities.capacitacao.update(registro._id, {
        aulasCompletas: novasCompletas,
        etapaAtual: proximaEtapa,
        certificadoEmitido: todasConcluidas || !!registro?.certificadoEmitido,
        dataCertificado: todasConcluidas
          ? registro?.dataCertificado ?? agora
          : registro?.dataCertificado,
        // tokenAcesso permanece inalterado (acesso ao curso via URL)
        // tokenCriarSenha é um campo SEPARADO exclusivo para criação de senha
        ...(todasConcluidas ? {
          tokenCriarSenha: novoToken,
          tokenCriarSenhaExpiracao: tokenExpira,
        } : {}),
        dataInicioAcesso,
        dataFimAcesso,
        updatedAt: agora,
      })

      const novoRegistro = {
        ...registro,
        aulasCompletas: novasCompletas,
        etapaAtual: proximaEtapa,
        certificadoEmitido: todasConcluidas || !!registro?.certificadoEmitido,
        dataCertificado: todasConcluidas
          ? registro?.dataCertificado ?? agora
          : registro?.dataCertificado,
        tokenAcesso: novoToken,
        tokenAcessoExpiracao: tokenExpira,
        dataInicioAcesso,
        dataFimAcesso,
      }
      setRegistro(novoRegistro)

      // Disparar e-mails ao concluir todas as aulas
      if (todasConcluidas) {
        await enviarEmailsConclusao(novoRegistro, novoToken, dataFimAcesso)
      }

      // Navegar APÓS o disparo do e-mail 4 (com anexo) ter completado
      setTimeout(() => {
        if (todasConcluidas) {
          navigate('/certificado')
        } else {
          fecharModal()
        }
      }, 900)
    } catch (e) {
      console.error('[curso] erro ao salvar progresso', e)
      setErro('Não foi possível salvar seu progresso. Tente novamente.')
    } finally {
      setSalvando(false)
    }
  }

  // ── Identificação de emergência: cria o registro em `capacitacao`, gera o ──
  // certificado e dispara os e-mails para quem concluiu o curso sem nenhum
  // registro vinculado (acesso direto/anônimo a /curso-aulas).
  async function confirmarIdentificacaoEConcluir() {
    setIdentErro('')
    if (!identNome.trim() || !identEmail.trim()) {
      setIdentErro('Por favor, preencha nome e e-mail.')
      return
    }
    if (!identEmail.includes('@') || !identEmail.includes('.')) {
      setIdentErro('Informe um e-mail válido.')
      return
    }
    const validacao = validarNumero(identWhatsapp, identPais)
    if (!validacao.valido) {
      setIdentErro(validacao.mensagem)
      return
    }
    if (!conclusaoPendente) {
      setIdentErro('Não foi possível localizar seu progresso. Recarregue a página e tente novamente.')
      return
    }

    setCriandoIdentidade(true)
    try {
      const digitsOnly = identWhatsapp.replace(/\D/g, '')
      const whatsappFormatado = identPais.codigo
        ? `${identPais.codigo}${digitsOnly}`
        : identWhatsapp.trim()

      const agora = new Date().toISOString()
      const dataFimAcesso = new Date(Date.now() + 1000 * 60 * 60 * 24 * 21).toISOString()
      const tokenAcesso = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}-${Math.random().toString(36).slice(2, 10)}`
      const exp7d = Date.now() + 1000 * 60 * 60 * 24 * 7
      const novoToken = btoa(JSON.stringify({ email: identEmail.trim().toLowerCase(), exp: exp7d }))
      const tokenExpira = new Date(exp7d).toISOString()

      const dadosRegistro: Record<string, unknown> = {
        nome: identNome.trim(),
        email: identEmail.trim().toLowerCase(),
        whatsapp: whatsappFormatado,
        status: '',
        origem: 'Curso',
        statusInscricao: 'Curso concluido',
        etapaAtual: 'certificado_emitido',
        aulasCompletas: conclusaoPendente.novasCompletas,
        certificadoEmitido: true,
        dataCertificado: agora,
        dataInscricao: agora,
        dataInicioAcesso: agora,
        dataFimAcesso,
        tokenAcesso,
        tokenAcessoExpiracao: dataFimAcesso,
        tokenCriarSenha: novoToken,
        tokenCriarSenhaExpiracao: tokenExpira,
        indicadoPor: 'monica',
        diagnosticoPreenchido: false,
        senhaCriada: false,
        creator: 'public',
        createdAt: agora,
        updatedAt: agora,
      }

      const criado = await lumi.entities.capacitacao.create(dadosRegistro)
      const idCriado = (criado as any)?._id ?? (criado as any)?.id ?? ''

      try {
        sessionStorage.setItem('capacitacao_id', idCriado)
        sessionStorage.setItem('capacitacao_whatsapp', whatsappFormatado)
        sessionStorage.setItem('capacitacao_nome', identNome.trim())
        sessionStorage.setItem('capacitacao_email', identEmail.trim())
        localStorage.setItem('capacitacao_id', idCriado)
        localStorage.setItem('capacitacao_whatsapp', whatsappFormatado)
        localStorage.setItem('capacitacao_nome', identNome.trim())
        localStorage.setItem('capacitacao_email', identEmail.trim())
      } catch {
        // ignore
      }

      const registroFinal = { ...dadosRegistro, _id: idCriado }
      setRegistro(registroFinal)

      await enviarEmailsConclusao(registroFinal, novoToken, dataFimAcesso)

      setPrecisaIdentificar(false)
      setConclusaoPendente(null)
      navigate('/certificado')
    } catch (e) {
      console.error('[curso] erro ao criar identificação e concluir curso', e)
      setIdentErro('Não foi possível concluir seu cadastro. Tente novamente.')
    } finally {
      setCriandoIdentidade(false)
    }
  }

  // ── Estados de carregamento ───────────────────────────────────────────────────

  if (carregando) {
    return (
      <div className="min-h-screen bg-[#F8F4EF] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#2D6A4F]" size={32} />
      </div>
    )
  }

  if (erro) {
    return (
      <div className="min-h-screen bg-[#F8F4EF] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 border border-[#EDE0D4] shadow-sm text-center">
          <AlertCircle className="mx-auto text-[#B08968] mb-3" size={36} />
          <p className="text-[#2C2C2A] mb-6">{erro}</p>
          <Link
            to="/selecao"
            className="inline-flex items-center gap-2 bg-[#2D6A4F] text-white font-medium px-6 py-3 rounded-full hover:bg-[#1f4f3a] transition-colors"
          >
            Ir para a seleção
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    )
  }

  const completas = aulasCompletas()
  const totalCompletas = completas.length
  const progresso = Math.round((totalCompletas / AULAS.length) * 100)
  const todasConcluidas = totalCompletas === AULAS.length

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#F8F4EF]">
      {/* ── Header ── */}
      <header className="bg-white border-b border-[#EDE0D4]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-[#B08968] font-medium">
              Diagnóstico Saúde e Emoções
            </p>
            <h1 className="font-cormorant text-xl sm:text-2xl font-semibold text-[#2D6A4F]">
              Uma Gota de Equilíbrio
            </h1>
          </div>
          <div className="text-right">
            <p className="text-xs text-[#5F5E5A]">Progresso</p>
            <p className="text-sm font-semibold text-[#2D6A4F] tabular-nums">
              {totalCompletas} de {AULAS.length} aulas
            </p>
          </div>
        </div>
        {/* Barra de progresso */}
        <div className="h-1.5 bg-[#EDE0D4]">
          <div
            className="h-full bg-[#52B788] transition-all duration-700"
            style={{ width: `${progresso}%` }}
          />
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Banner de conclusão */}
        {todasConcluidas && (
          <div className="bg-[#D8F3DC] border border-[#52B788] rounded-2xl p-5 sm:p-6 mb-8 flex items-start gap-4">
            <Award className="text-[#2D6A4F] shrink-0 mt-0.5" size={28} />
            <div className="flex-1">
              <h3 className="font-cormorant text-xl font-semibold text-[#2D6A4F] mb-1">
                Parabéns! Você concluiu todas as aulas! 🎉
              </h3>
              <p className="text-sm text-[#2C2C2A] mb-3">
                Seu certificado de Aromaterapia Introdutória está disponível.
              </p>
              <Link
                to="/certificado"
                className="inline-flex items-center gap-2 bg-[#2D6A4F] text-white text-sm font-medium px-5 py-2.5 rounded-full hover:bg-[#1f4f3a] transition-colors"
              >
                Ver meu certificado
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        )}

        {/* Grade de aulas */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          {AULAS.map(aula => {
            const liberada = aulaLiberada(aula.numero)
            const concluida = aulaConcluida(aula.id)
            return (
              <button
                key={aula.id}
                type="button"
                onClick={() => abrirAula(aula)}
                disabled={!liberada}
                className={`text-left rounded-2xl p-5 sm:p-6 border transition-all ${
                  concluida
                    ? 'bg-[#D8F3DC] border-[#52B788]'
                    : liberada
                      ? 'bg-white border-[#EDE0D4] hover:border-[#52B788] hover:shadow-md cursor-pointer'
                      : 'bg-white border-[#EDE0D4] opacity-60 cursor-not-allowed'
                }`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <span className="text-xs uppercase tracking-wider text-[#B08968] font-medium">
                    Aula {aula.numero}
                  </span>
                  {concluida ? (
                    <CheckCircle2 className="text-[#2D6A4F] shrink-0" size={20} />
                  ) : liberada ? (
                    <PlayCircle className="text-[#52B788] shrink-0" size={20} />
                  ) : (
                    <Lock className="text-[#5F5E5A] shrink-0" size={18} />
                  )}
                </div>
                <h3 className="font-cormorant text-lg sm:text-xl font-semibold text-[#2C2C2A] mb-1.5 leading-snug">
                  {aula.titulo}
                </h3>
                <p className="text-sm text-[#5F5E5A] leading-relaxed">{aula.descricao}</p>
                {!liberada && (
                  <p className="mt-2 text-xs text-[#B08968]">
                    🔒 Complete a aula anterior para liberar
                  </p>
                )}
              </button>
            )
          })}
        </div>

        {/* ── Bloco final — diagnóstico ── */}
        <div className="mt-10 sm:mt-12 bg-white border border-[#EDE0D4] rounded-3xl p-6 sm:p-10 text-center shadow-sm">
          <Leaf className="mx-auto text-[#52B788] mb-3" size={30} />
          <p className="font-cormorant text-xl sm:text-2xl font-semibold text-[#2C2C2A] mb-5 leading-snug max-w-xl mx-auto">
            Quer descobrir quais óleos essenciais o seu corpo e mente precisam agora?
          </p>
          <Link
            to="/resultado"
            className="inline-flex items-center gap-2 bg-[#2D6A4F] text-white font-medium px-7 py-3.5 rounded-full hover:bg-[#1f4f3a] transition-colors"
          >
            Fazer meu diagnóstico gratuito
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>

      {/* ── Modal de aula ── */}
      {aulaAtiva && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center px-3 py-4 overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-5 sm:p-8 my-4">
            {/* Cabeçalho do modal */}
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <span className="text-xs uppercase tracking-wider text-[#B08968] font-medium">
                  Aula {aulaAtiva.numero} de {AULAS.length}
                </span>
                <h2 className="font-cormorant text-2xl sm:text-3xl font-semibold text-[#2C2C2A] leading-tight">
                  {aulaAtiva.titulo}
                </h2>
                <p className="text-sm text-[#5F5E5A] mt-1 leading-relaxed">
                  {aulaAtiva.descricao}
                </p>
              </div>
              <button
                type="button"
                onClick={fecharModal}
                className="text-[#5F5E5A] hover:text-[#2C2C2A] text-2xl leading-none flex-shrink-0 mt-1"
                aria-label="Fechar"
              >
                ×
              </button>
            </div>

            {/* ── ETAPA 1: Assistir vídeo ── */}
            <div className="mb-5">
              <div className="aspect-video rounded-2xl overflow-hidden bg-black">
                <iframe
                  src={aulaAtiva.videoUrl}
                  title={aulaAtiva.titulo}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>

              {!assistiu && (
                <button
                  type="button"
                  onClick={() => setAssistiu(true)}
                  className="mt-3 w-full flex items-center justify-center gap-2 bg-[#52B788] text-white font-medium px-5 py-3 rounded-full hover:bg-[#3da572] transition-colors"
                >
                  <CheckCircle2 size={18} />
                  Marcar que assisti o vídeo
                </button>
              )}
              {assistiu && (
                <div className="mt-3 flex items-center gap-2 text-[#2D6A4F] text-sm font-medium">
                  <CheckCircle2 size={16} />
                  Vídeo assistido ✓
                </div>
              )}
            </div>

            {/* ── ETAPA 2: Quiz (liberado somente após marcar que assistiu) ── */}
            {assistiu && (
              <div className="bg-[#F8F4EF] border border-[#EDE0D4] rounded-2xl p-5">
                <p className="text-xs uppercase tracking-wider text-[#B08968] font-medium mb-1">
                  Quiz da aula
                </p>
                <p className="text-sm text-[#5F5E5A] mb-5">
                  Responda as 3 perguntas abaixo. Você precisa acertar pelo menos 2 para avançar.
                </p>

                <div className="space-y-6">
                  {aulaAtiva.quiz.map((pergunta, pIdx) => (
                    <div key={pIdx}>
                      <h3 className="font-cormorant text-base sm:text-lg font-semibold text-[#2C2C2A] mb-3 leading-snug">
                        {pIdx + 1}. {pergunta.enunciado}
                      </h3>
                      <div className="space-y-2">
                        {pergunta.opcoes.map((op, oIdx) => {
                          const selecionada = respostas[pIdx] === oIdx
                          const temFeedback = feedbackQuiz !== null
                          const eCorreta = oIdx === pergunta.correta
                          const eErrada = temFeedback && selecionada && !eCorreta

                          let classeOpcao =
                            'flex items-start gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-all text-sm'
                          if (temFeedback && eCorreta) {
                            classeOpcao += ' border-[#52B788] bg-[#D8F3DC]'
                          } else if (eErrada) {
                            classeOpcao += ' border-red-400 bg-red-50'
                          } else if (selecionada) {
                            classeOpcao += ' border-[#52B788] bg-[#D8F3DC]'
                          } else {
                            classeOpcao += ' border-[#EDE0D4] bg-white hover:border-[#52B788]/50'
                          }

                          return (
                            <label key={oIdx} className={classeOpcao}>
                              <input
                                type="radio"
                                name={`quiz-${pIdx}`}
                                checked={selecionada}
                                onChange={() => {
                                  if (feedbackQuiz !== null) return
                                  const novas = [...respostas] as (number | null)[]
                                  novas[pIdx] = oIdx
                                  setRespostas(novas)
                                }}
                                disabled={feedbackQuiz !== null}
                                className="accent-[#2D6A4F] mt-0.5 flex-shrink-0"
                              />
                              <span className="text-[#2C2C2A]">{op}</span>
                            </label>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Feedback */}
                {feedbackQuiz !== null && (
                  <div
                    className={`mt-5 rounded-xl px-4 py-3 text-sm font-medium flex items-start gap-2 ${
                      feedbackQuiz.acertos >= 2
                        ? 'bg-[#D8F3DC] text-[#2D6A4F]'
                        : 'bg-red-50 text-[#991B1B]'
                    }`}
                  >
                    {feedbackQuiz.acertos >= 2 ? (
                      <>
                        <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
                        <span>
                          Você acertou {feedbackQuiz.acertos} de 3 perguntas. Parabéns! Avançando…
                        </span>
                      </>
                    ) : (
                      <>
                        <AlertCircle size={16} className="mt-0.5 shrink-0" />
                        <span>
                          Você acertou {feedbackQuiz.acertos} de 3 perguntas. Revise o conteúdo da aula e tente novamente.
                        </span>
                      </>
                    )}
                  </div>
                )}

                {/* Botões */}
                <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
                  {feedbackQuiz !== null && feedbackQuiz.acertos < 2 ? (
                    <button
                      type="button"
                      onClick={() => {
                        setRespostas([null, null, null])
                        setFeedbackQuiz(null)
                      }}
                      className="inline-flex items-center gap-2 border-2 border-[#2D6A4F] text-[#2D6A4F] font-medium px-5 py-2.5 rounded-full hover:bg-[#D8F3DC] transition-colors"
                    >
                      <RefreshCw size={16} />
                      Tentar novamente
                    </button>
                  ) : feedbackQuiz === null ? (
                    <button
                      type="button"
                      onClick={confirmarQuiz}
                      disabled={respostas.some(r => r === null) || salvando}
                      className="inline-flex items-center gap-2 bg-[#2D6A4F] text-white font-medium px-6 py-3 rounded-full hover:bg-[#1f4f3a] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {salvando ? (
                        <>
                          <Loader2 className="animate-spin" size={16} />
                          Salvando…
                        </>
                      ) : (
                        <>
                          Confirmar respostas
                          <ArrowRight size={16} />
                        </>
                      )}
                    </button>
                  ) : null}

                  <button
                    type="button"
                    onClick={fecharModal}
                    className="text-sm text-[#5F5E5A] hover:text-[#2D6A4F] transition-colors"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            )}

            {/* Hint para assistir primeiro */}
            {!assistiu && (
              <p className="mt-3 text-xs text-[#B08968] text-center">
                Assista ao vídeo e marque que assistiu para liberar o quiz.
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── Modal de identificação de emergência (conclusão sem registro vinculado) ── */}
      {precisaIdentificar && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center px-3 py-4 overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-8 my-4">
            <Award className="text-[#2D6A4F] mb-3" size={32} />
            <h2 className="font-cormorant text-2xl font-semibold text-[#2C2C2A] mb-1 leading-tight">
              Parabéns por concluir o curso!
            </h2>
            <p className="text-sm text-[#5F5E5A] mb-5 leading-relaxed">
              Para emitir seu certificado, precisamos confirmar seus dados.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#B08968] font-medium mb-1.5">
                  Nome completo
                </label>
                <input
                  type="text"
                  value={identNome}
                  onChange={e => setIdentNome(e.target.value)}
                  disabled={criandoIdentidade}
                  placeholder="Seu nome completo"
                  className="w-full px-4 py-3 border-2 border-[#EDE0D4] rounded-xl focus:outline-none focus:border-[#52B788] bg-white disabled:bg-gray-100 disabled:text-gray-400"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-[#B08968] font-medium mb-1.5">
                  E-mail
                </label>
                <input
                  type="email"
                  value={identEmail}
                  onChange={e => setIdentEmail(e.target.value)}
                  disabled={criandoIdentidade}
                  placeholder="seu@email.com"
                  className="w-full px-4 py-3 border-2 border-[#EDE0D4] rounded-xl focus:outline-none focus:border-[#52B788] bg-white disabled:bg-gray-100 disabled:text-gray-400"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-[#B08968] font-medium mb-1.5">
                  WhatsApp
                </label>
                <PhoneInput
                  value={identWhatsapp}
                  onChange={(numero, pais) => {
                    setIdentWhatsapp(numero)
                    setIdentPais(pais)
                  }}
                  disabled={criandoIdentidade}
                  paisInicial={identPais}
                />
              </div>
            </div>

            {identErro && (
              <div className="mt-4 rounded-xl px-4 py-3 text-sm font-medium bg-red-50 text-[#991B1B] flex items-start gap-2">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{identErro}</span>
              </div>
            )}

            <button
              type="button"
              onClick={confirmarIdentificacaoEConcluir}
              disabled={criandoIdentidade}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#2D6A4F] text-white font-medium px-6 py-3.5 rounded-full hover:bg-[#1f4f3a] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {criandoIdentidade ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Emitindo certificado…
                </>
              ) : (
                <>
                  Confirmar e emitir certificado
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Alerta de erro global (salvar) */}
      {erro && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-red-600 text-white px-5 py-3 rounded-2xl shadow-lg text-sm z-50 flex items-center gap-2">
          <AlertCircle size={16} />
          {erro}
        </div>
      )}
    </div>
  )
}

export default Curso
